import React, { useEffect, useState } from 'react';
import { upcomingProjectsData as mockUpcomingProjectsData } from '../data/mock';

const UpcomingProjectsSection = ({ data, sectionId = 'upcoming-projects' }) => {
  const projects = Array.isArray(data) ? data : [data || mockUpcomingProjectsData];
  const [activePoster, setActivePoster] = useState(null);

  useEffect(() => {
    if (!activePoster) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActivePoster(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activePoster]);

  return (
    <section
      id={sectionId}
      className="relative overflow-hidden bg-[#1A532F] py-16 px-6"
    >
      <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(to_right,#FFFFFF1F_1px,transparent_1px),linear-gradient(to_bottom,#FFFFFF1F_1px,transparent_1px)] [background-size:28px_28px]" />
      <div className="pointer-events-none absolute -top-24 -right-10 h-72 w-72 rounded-full bg-[#F5B041]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-[#5B9BD5]/20 blur-3xl" />

      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <p
            className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F3EFE4]"
            style={{ fontFamily: "'Press Start 2P', monospace" }}
          >
            Upcoming Projects
          </p>
        </div>

        <div className="space-y-8">
          {projects.map((project, index) => {
            const hasSignupLink = project.signupLink && project.signupLink !== '#';
            const [datesPart, timePart] = (project.dateTime || '').split('|').map((item) => item?.trim());

            return (
              <div
                key={`${project.title}-${index}`}
                className="relative border-4 border-[#1A532F] bg-[#FCF8ED] shadow-[12px_12px_0_0_#1A532F]"
              >
                <div className="grid lg:grid-cols-[1.32fr_0.68fr] gap-0">
                  <div className="relative border-b-4 border-[#1A532F] lg:border-b-0 lg:border-r-4">
                    <div className="absolute top-4 left-4 z-10 bg-[#E64A2B] text-white text-xs md:text-sm font-bold px-3 py-1.5">
                      {project.label}
                    </div>
                    <div className="bg-[#EEE8D8] p-4 md:px-6 md:pt-6 md:pb-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-start justify-items-center">
                      {project.infoPosterUrl && (
                        <button
                          type="button"
                          onClick={() => setActivePoster({ src: project.infoPosterUrl, alt: `${project.title} workshop information poster` })}
                          className="block w-full text-left mx-auto self-start"
                        >
                          <img
                            src={project.infoPosterUrl}
                            alt={`${project.title} workshop information poster`}
                            className="block w-full h-auto max-h-[920px] object-contain bg-white border-2 border-[#1A532F] cursor-zoom-in mx-auto"
                          />
                        </button>
                      )}
                      {project.posterUrl && (
                        <button
                          type="button"
                          onClick={() => setActivePoster({ src: project.posterUrl, alt: `${project.title} poster` })}
                          className="block w-full text-left mx-auto self-start"
                        >
                          <img
                            src={project.posterUrl}
                            alt={`${project.title} poster`}
                            className="block w-full h-auto max-h-[920px] object-contain bg-white border-2 border-[#1A532F] cursor-zoom-in mx-auto"
                          />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-5 md:p-6 lg:p-7 flex flex-col">
                    <h3 className="text-[#143D24] text-2xl md:text-3xl font-black uppercase leading-tight mb-3">{project.title}</h3>
                    {project.descriptionIntro ? (
                      <div className="mb-4">
                        <p className="text-[#1A532F] text-sm leading-6 italic">{project.descriptionIntro}</p>
                        <p className="text-[#1A532F] text-sm leading-6 mt-2 whitespace-pre-line">{project.description}</p>
                      </div>
                    ) : (
                      <p className="text-[#1A532F] text-sm leading-6 mb-4 whitespace-pre-line">{project.description}</p>
                    )}

                    <div className="grid sm:grid-cols-2 gap-2.5 mb-5">
                      <div className="border-2 border-[#1A532F] bg-white p-2.5">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#1A532F]/70 mb-1">Dates</p>
                        <p className="text-[#1A532F] text-sm font-semibold">{datesPart || project.dateTime}</p>
                      </div>
                      <div className="border-2 border-[#1A532F] bg-white p-2.5">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#1A532F]/70 mb-1">Time</p>
                        <p className="text-[#1A532F] text-sm font-semibold">{timePart || '10:00 AM to 1:00 PM'}</p>
                      </div>
                      <div className="border-2 border-[#1A532F] bg-white p-2.5">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#1A532F]/70 mb-1">Venue</p>
                        <p className="text-[#1A532F] text-sm font-semibold">{project.location}</p>
                      </div>
                      <div className="border-2 border-[#1A532F] bg-white p-2.5">
                        <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#1A532F]/70 mb-1">Seats</p>
                        <p className="text-[#E64A2B] text-sm font-black">{project.seats}</p>
                      </div>
                      {project.fee && (
                        <div className="border-2 border-[#1A532F] bg-white p-2.5 sm:col-span-2">
                          <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#1A532F]/70 mb-1">Fee</p>
                          <p className="text-[#1A532F] text-sm font-semibold">{project.fee}</p>
                        </div>
                      )}
                    </div>

                    <a
                      href={project.signupLink}
                      target={hasSignupLink ? '_blank' : undefined}
                      rel={hasSignupLink ? 'noopener noreferrer' : undefined}
                      aria-disabled={!hasSignupLink}
                      className={`inline-flex items-center justify-center px-8 py-3 text-white font-black uppercase tracking-[0.08em] transition-all ${
                        hasSignupLink
                          ? 'bg-[#1A532F] hover:bg-[#133f24] hover:-translate-y-0.5 shadow-[0_6px_0_0_#0f2a18]'
                          : 'bg-[#1A532F]/50 cursor-not-allowed shadow-none'
                      }`}
                      onClick={(event) => {
                        if (!hasSignupLink) event.preventDefault();
                      }}
                    >
                      {project.signupText}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activePoster && (
        <div
          className="fixed inset-0 z-[100] bg-black/45 backdrop-blur-md p-4 md:p-8 flex items-center justify-center"
          onClick={() => setActivePoster(null)}
          role="presentation"
        >
          <div className="relative inline-flex max-w-[92vw] max-h-[92vh] items-center justify-center">
            <img
              src={activePoster.src}
              alt={activePoster.alt}
              className="w-auto h-auto max-w-[92vw] max-h-[88vh] object-contain"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default UpcomingProjectsSection;
